import React, { useState } from "react";
import { Loader2, AlertTriangle, CheckCircle2, ShieldAlert } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/sre-agent`;

const HIGH_PHRASE = "CONFIRM DEPLOY";

const ApprovalPrompt = ({ sessionId, pending, onResolved }) => {
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  if (!pending) return null;

  const isHigh = pending.requires_typed_confirmation;
  const phrase = pending.typed_confirmation_phrase || HIGH_PHRASE;
  const canSubmit = isHigh ? typed.trim() === phrase : true;

  const submit = async (approved) => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/approve`, {
        session_id: sessionId,
        action_id: pending.action_id,
        approved,
        typed_confirmation: isHigh ? typed.trim() : null,
      });
      setResult(data);
      if (data.status !== "pending_approval") {
        onResolved && onResolved(data);
      }
    } catch (e) {
      setResult({ status: "failed", message: e?.message || "Network error" });
    } finally {
      setBusy(false);
    }
  };

  if (result && result.status !== "pending_approval") {
    const ok = result.status === "succeeded";
    return (
      <div
        className={`mt-2 rounded-lg border p-3 text-sm ${
          ok
            ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200"
            : "border-red-500/40 bg-red-500/10 text-red-200"
        }`}
        data-testid="sre-approval-result"
      >
        <div className="flex items-center gap-2 font-medium">
          {ok ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
          {pending.tool_name} → {result.status}
          {typeof result.exit_code === "number" && (
            <span className="ml-auto text-xs opacity-80">exit_code={result.exit_code}</span>
          )}
        </div>
        {result.output && (
          <pre className="mt-2 whitespace-pre-wrap text-xs leading-snug max-h-48 overflow-auto">
            {result.output}
          </pre>
        )}
        {result.message && !result.output && (
          <p className="mt-1 text-xs">{result.message}</p>
        )}
      </div>
    );
  }

  return (
    <div
      className={`mt-2 rounded-lg border p-3 ${
        isHigh ? "border-red-500/40 bg-red-500/10" : "border-amber-500/40 bg-amber-500/10"
      }`}
      data-testid="sre-approval-prompt"
    >
      <div className="flex items-center gap-2 text-sm font-medium text-white">
        <ShieldAlert className="w-4 h-4" />
        Approval required · <span className="font-mono">{pending.tool_name}</span>
        <span
          className={`ml-auto px-2 py-0.5 rounded text-[10px] font-semibold ${
            isHigh ? "bg-red-500/30 text-red-200" : "bg-amber-500/30 text-amber-200"
          }`}
        >
          {pending.risk_level}
        </span>
      </div>
      <p className="mt-2 text-xs text-gray-300">{pending.summary}</p>

      {isHigh && (
        <>
          <p className="mt-3 text-xs text-red-200">
            Type <span className="font-mono font-semibold">{phrase}</span> to confirm.
          </p>
          <input
            type="text"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            disabled={busy}
            data-testid="sre-typed-confirm-input"
            placeholder={phrase}
            className="mt-1 w-full rounded bg-black/40 border border-white/10 px-2 py-1 text-sm font-mono text-white focus:outline-none focus:border-red-400"
          />
        </>
      )}

      <div className="mt-3 flex gap-2 justify-end">
        <button
          onClick={() => submit(false)}
          disabled={busy}
          data-testid="sre-approval-reject"
          className="px-3 py-1.5 text-xs rounded bg-gray-700 hover:bg-gray-600 text-white disabled:opacity-50"
        >
          Reject
        </button>
        <button
          onClick={() => submit(true)}
          disabled={busy || !canSubmit}
          data-testid="sre-approval-approve"
          className={`px-3 py-1.5 text-xs rounded text-white flex items-center gap-1 ${
            isHigh ? "bg-red-600 hover:bg-red-500" : "bg-amber-600 hover:bg-amber-500"
          } disabled:opacity-50`}
        >
          {busy && <Loader2 className="w-3 h-3 animate-spin" />}
          {isHigh ? "Confirm & Run" : "Approve"}
        </button>
      </div>
    </div>
  );
};

export default ApprovalPrompt;
